/**
 * Sphere attachment types
 *
 * Covers both free-form collection attachments (files and external links)
 * and named slot attachments (avatar, future banner/icon/cover). Row field
 * names are snake_case to match the Postgres column names in the
 * `sphere_attachments` and `organization_storage_usage` tables.
 */

export type AttachmentType = 'file' | 'link';

export type SlotName = 'avatar' | (string & {});

/**
 * Lifecycle state of the malware-scan verdict applied to the row's blob.
 *
 * - `pending`: row is freshly uploaded; Defender has not yet reported a result.
 * - `clean`: Defender returned a clean verdict; content routes serve the blob.
 * - `malicious`: Defender flagged the blob; the `scanResultHandler` deletes the
 *   row + blob, so this state is a transient window before the row disappears.
 * - `quarantined`: manually held by operations tooling (e.g. policy review);
 *   distinct from `malicious` because there is no automatic deletion.
 */
export type ScanStatus = 'pending' | 'clean' | 'malicious' | 'quarantined';

export interface AttachmentsConfig {
  enabled: boolean;
  allowedTypes: AttachmentType[];
}

export type SlotType = 'image' | 'file' | 'link';

export interface SlotConfig {
  type: SlotType;
  mimeTypes?: string[];
  maxSize?: number;
  resize?: { full?: number; thumbnail?: number };
  timelineVisible: boolean;
  storageMeteringScope: string;
}

export interface SphereSchemaAttachmentsConfig {
  attachments: AttachmentsConfig;
  slots: Partial<Record<SlotName, SlotConfig>>;
}

export interface SphereAttachment {
  id: string;
  sphere_id: string;
  organization_id: string;
  slot_name: string | null;
  attachment_type: AttachmentType;
  name: string;
  description: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  blob_path: string | null;
  url: string | null;
  metadata: Record<string, unknown> | null;
  uploaded_by: string;
  uploaded_at: string;
  scan_status: ScanStatus;
}

export interface OrganizationStorageUsage {
  organization_id: string;
  scope: string;
  attachment_count: number;
  attachment_bytes_total: number;
  updated_at: string;
}

/**
 * Canonical set of MIME types the platform is willing to render inline
 * (thumbnail previews, embedded image/video tags, etc.). Anything outside
 * this set must be surfaced as a generic file badge with a download action.
 *
 * Shared by frontend preview components and backend content-sniffing so the
 * two layers cannot disagree. Exact-match only — NEVER prefix-match on
 * `image/` or `video/` since SVG (`image/svg+xml`) and other exotic types
 * would slip through.
 */
export const INLINE_PREVIEW_ALLOWLIST: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'video/mp4',
  'video/webm',
  'video/ogg',
]);
